import React, { useCallback, useEffect, useState } from "react";
import { Database, Cloud, Download, Loader2, RefreshCw, HardDrive } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { formatApiError, API_BASE, STORAGE_KEY } from "@/lib/api";
import BackupRow from "@/components/backup/BackupRow";
import RestoreFromFileButton from "@/components/backup/RestoreFromFileButton";

/**
 * Admin-only Backup tab.
 *
 * Three-block layout:
 *   1. **Header** — Backup Now / Restore from file / auto-backup toggles.
 *   2. **Environment sync** — preview-only "Sync from live" button.
 *   3. **Backup list table** — every backup on this env, newest first.
 *
 * Data comes from a single `/api/admin/backups` fetch that returns the
 * rows AND the toggle states, so we don't have to interleave two
 * request paths for the same page load.
 */
export default function BackupTab() {
  const [rows, setRows] = useState([]);
  const [toggles, setToggles] = useState({ auto_backup_eod_enabled: true, auto_backup_deploy_enabled: true });
  const [retention, setRetention] = useState({});
  const [syncConfigured, setSyncConfigured] = useState(false);
  const [isPreviewEnv, setIsPreviewEnv] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pendingRestore, setPendingRestore] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncPlan, setSyncPlan] = useState(null); // dry-run result shown in the confirm dialog
  const [syncPlanLoading, setSyncPlanLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/backups");
      setRows(r.data.rows || []);
      setToggles(r.data.toggles || toggles);
      setRetention(r.data.retention || {});
      setSyncConfigured(!!r.data.sync_configured);
      setIsPreviewEnv(!!r.data.is_preview_env);
    } catch (err) {
      toast.error("Couldn't load backups", { description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const takeBackup = async () => {
    setBusy(true);
    try {
      await api.post("/admin/backups");
      toast.success("Backup taken");
      load();
    } catch (err) {
      toast.error("Backup failed", { description: formatApiError(err) });
    } finally {
      setBusy(false);
    }
  };

  const doDownload = (row) => {
    // Download via a direct URL with the bearer token as a query param so
    // the browser can trigger its native download dialog. Since our
    // ingress only forwards /api/* to FastAPI, we build the URL manually.
    const token = localStorage.getItem(STORAGE_KEY);
    const url = `${API_BASE}/admin/backups/${row.id}/download`;
    // Fetch as blob so we can control the filename + include auth header.
    api
      .get(`/admin/backups/${row.id}/download`, { responseType: "blob" })
      .then((r) => {
        const blob = new Blob([r.data], { type: "application/gzip" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = row.filename;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          URL.revokeObjectURL(a.href);
          a.remove();
        }, 1000);
      })
      .catch((err) => toast.error("Download failed", { description: formatApiError(err) }));
    // Suppress lint for unused vars
    void url; void token;
  };

  const confirmRestore = async () => {
    if (!pendingRestore) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/backups/${pendingRestore.id}/restore`);
      const total = Object.values(r.data?.loaded || {}).reduce((a, b) => a + b, 0);
      toast.success(`Restored — ${total} rows loaded`);
      setPendingRestore(null);
      load();
    } catch (err) {
      toast.error("Restore failed", { description: formatApiError(err) });
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await api.delete(`/admin/backups/${pendingDelete.id}`);
      toast.success("Backup deleted");
      setPendingDelete(null);
      load();
    } catch (err) {
      toast.error("Delete failed", { description: formatApiError(err) });
    } finally {
      setBusy(false);
    }
  };

  const toggleAutoBackup = async (name, value) => {
    setToggles((t) => ({ ...t, [name]: value }));
    try {
      await api.patch(`/admin/backups/toggles/${name}`, { value });
    } catch (err) {
      toast.error("Toggle failed", { description: formatApiError(err) });
      load();
    }
  };

  const openSyncConfirm = async () => {
    setSyncPlanLoading(true);
    try {
      const r = await api.get("/admin/sync/preview");
      setSyncPlan(r.data);
    } catch (err) {
      toast.error("Couldn't reach live", { description: formatApiError(err) });
    } finally {
      setSyncPlanLoading(false);
    }
  };

  const syncFromLive = async () => {
    setSyncPlan(null);
    setSyncing(true);
    try {
      const r = await api.post("/admin/sync/from-live");
      const total = Object.values(r.data?.loaded || {}).reduce((a, b) => a + b, 0);
      const m = r.data?.merged || {};
      toast.success(`Synced from live — ${total} rows replaced`, {
        description: `Accounts +${m.users?.added ?? 0}/~${m.users?.updated ?? 0} · locations +${m.locations?.added ?? 0}/~${m.locations?.updated ?? 0} · passwords untouched`,
      });
      load();
    } catch (err) {
      toast.error("Sync failed", { description: formatApiError(err) });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="backup-tab">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Backup &amp; Sync</h1>
          <p className="text-sm text-slate-500 mt-1">
            Snapshots of every included collection. Files live on pod disk —
            download anything you want to keep durably.
          </p>
        </div>
        <div className="flex gap-2 shrink-0 flex-wrap">
          <RestoreFromFileButton onDone={load} />
          <Button
            onClick={takeBackup}
            disabled={busy}
            className="text-white"
            style={{ background: "hsl(var(--brand))" }}
            data-testid="backup-take-now-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Backup Now
          </Button>
        </div>
      </div>

      {/* Auto-backup toggles */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-4">
        <h2 className="text-sm font-medium text-slate-700 flex items-center gap-2">
          <HardDrive className="h-4 w-4 text-slate-400" />
          Automatic backups
        </h2>
        <ToggleRow
          label="End-of-day backup"
          description={`Snapshots this environment once per day when something changed. Keeps the ${retention.eod || 30} most-recent locally.`}
          checked={toggles.auto_backup_eod_enabled}
          onChange={(v) => toggleAutoBackup("auto_backup_eod_enabled", v)}
          testid="backup-toggle-eod"
        />
        <ToggleRow
          label="On-deploy backup"
          description={`Snapshots this environment before new code takes over. Keeps the ${retention.deploy || 10} most-recent locally.`}
          checked={toggles.auto_backup_deploy_enabled}
          onChange={(v) => toggleAutoBackup("auto_backup_deploy_enabled", v)}
          testid="backup-toggle-deploy"
        />
      </div>

      {/* Preview-only sync block */}
      {isPreviewEnv && (
        <div className="rounded-lg border border-brand/30 bg-brand/5 p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-sm font-medium text-slate-800 flex items-center gap-2">
                <Cloud className="h-4 w-4" />
                Sync from Live
              </h2>
              <p className="text-xs text-slate-600 mt-1 max-w-2xl">
                Pull a fresh snapshot from your live environment. <strong>Replaces</strong> CRM data (realtors, brokerages,
                outreach log, templates, scraping settings, social follows). <strong>Merges</strong> accounts, locations and
                territories — preview-only ones are kept, and passwords are never copied (new accounts get the preview default).
                Billing records are never synced.
              </p>
            </div>
            <Button
              onClick={openSyncConfirm}
              disabled={syncing || syncPlanLoading || !syncConfigured}
              variant="outline"
              data-testid="sync-from-live-btn"
              title={!syncConfigured ? "Configure LIVE_BACKEND_URL and SYNC_SHARED_SECRET in backend/.env" : ""}
            >
              {syncing || syncPlanLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              {syncConfigured ? "Sync from Live" : "Configure sync first"}
            </Button>
          </div>
        </div>
      )}

      {/* Backup list */}
      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <Database className="h-4 w-4 text-slate-400" />
          <h2 className="text-sm font-medium text-slate-700">All backups</h2>
          <span className="text-xs text-slate-500 tabular-nums">({rows.length})</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            disabled={loading}
            className="ml-auto"
            data-testid="backup-refresh"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </Button>
        </div>
        {rows.length === 0 && !loading ? (
          <div className="px-4 py-12 text-center text-sm text-slate-500">
            No backups yet — hit "Backup Now" above to take your first one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Type</th>
                  <th className="px-3 py-2 font-medium">Taken</th>
                  <th className="px-3 py-2 font-medium">Rows</th>
                  <th className="px-3 py-2 font-medium">Size</th>
                  <th className="px-3 py-2 font-medium">Storage</th>
                  <th className="px-3 py-2 font-medium">By</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <BackupRow
                    key={r.id}
                    row={r}
                    onDownload={doDownload}
                    onRestore={setPendingRestore}
                    onDelete={setPendingDelete}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Sync from Live confirmation (dry-run numbers from /admin/sync/preview) */}
      <AlertDialog open={!!syncPlan} onOpenChange={(o) => !o && setSyncPlan(null)}>
        <AlertDialogContent data-testid="sync-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Sync this preview from live?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-slate-600">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-rose-700">Replaced (preview copy wiped)</div>
                  <ul className="mt-1 text-xs grid grid-cols-2 gap-x-4" data-testid="sync-replaced-list">
                    {(syncPlan?.replaced_collections || []).map((c) => (
                      <li key={c} className="flex justify-between"><span>{c.replace("_", " ")}</span><span className="tabular-nums text-slate-500">{syncPlan.incoming_row_counts?.[c] ?? 0}</span></li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Merged (preview-only rows kept)</div>
                  <ul className="mt-1 text-xs grid grid-cols-2 gap-x-4" data-testid="sync-merged-list">
                    {(syncPlan?.merged_collections || []).map((c) => (
                      <li key={c} className="flex justify-between"><span>{c}</span><span className="tabular-nums text-slate-500">{syncPlan.incoming_row_counts?.[c] ?? 0} from live</span></li>
                    ))}
                  </ul>
                </div>
                <p className="text-xs" data-testid="sync-password-note">
                  Passwords are <strong>not</strong> copied — existing preview accounts keep theirs; accounts new to preview get the default preview password. Billing records are never synced.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={syncing} data-testid="sync-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={syncFromLive} disabled={syncing} data-testid="sync-confirm-go">
              {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Sync from Live
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restore confirmation */}
      <AlertDialog open={!!pendingRestore} onOpenChange={(o) => !o && setPendingRestore(null)}>
        <AlertDialogContent data-testid="backup-restore-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Restore this backup?</AlertDialogTitle>
            <AlertDialogDescription>
              This will wipe every included collection in <strong>this environment</strong> and
              replace it with the snapshot from{" "}
              <strong>
                {pendingRestore?.created_at ? new Date(pendingRestore.created_at).toLocaleString() : "?"}
              </strong>
              . Not reversible. Take a fresh backup first if you might want to come back.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRestore}
              disabled={busy}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="backup-restore-confirm"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Wipe and restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent data-testid="backup-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this backup?</AlertDialogTitle>
            <AlertDialogDescription>
              Removes the local file and hides the row from this list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={busy}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="backup-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ToggleRow({ label, description, checked, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="text-sm text-slate-800">{label}</p>
        <p className="text-xs text-slate-500 mt-0.5">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}
